import * as security from '@/common/security'

function defaultWidgetOrder () {
  return [
    'LiveTrackingWidget',
    'ZonesWidget',
    'ClientPoiWidget',
    'RecentClientsWidget',
    'RecentVehiclesWidget',
    'RecentUnitsWidget',
    'FindClientsWidget',
    'FindVehiclesWidget',
    'FindUnitsWidget',
    'TripReplayWidget',
    'PositionReplayWidget',
    'ToolsFindLocationWidget',
    'ToolsDirectionsWidget',
    'ToolsMeasurementWidget',
    'ToolsSendCommandWidget',
    'OptionsWidget'
  ]
}

function defaultWidgets () {
  return {
    LiveTrackingWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Live Tracking`,
      icon: 'crosshairs',
      secondaryIcon: null,
      canShow: () => true
    },
    TripReplayWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Trip Replay`,
      icon: 'road',
      secondaryIcon: null,
      canShow: () => true
    },
    PositionReplayWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Position Replay`,
      icon: 'boot',
      secondaryIcon: null,
      canShow: () => true
    },
    ZonesWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Zones`,
      icon: 'draw-polygon',
      secondaryIcon: null,
      canShow: () => security.canView('client_zones') || security.canView('monitoring_zones')
    },
    ClientPoiWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Points of Interest`,
      icon: 'map-marker-alt',
      secondaryIcon: null,
      canShow: () => security.canView('client_poi')
    },
    FindClientsWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Find`,
      caption: `Find Clients`,
      icon: 'handshake',
      secondaryIcon: 'search',
      canShow: () => {
        return security.isAboveClient() || (security.canView('clients') && security.belongsToMultipleClients())
      }
    },
    RecentClientsWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Recent`,
      caption: `Recently Accessed Clients`,
      icon: 'handshake',
      secondaryIcon: 'repeat',
      canShow: () => {
        return security.isAboveClient() || (security.canView('clients') && security.belongsToMultipleClients())
      }
    },
    RecentVehiclesWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Recent`,
      caption: `Recently Monitored Vehicles`,
      icon: 'car',
      secondaryIcon: 'repeat',
      canShow: () => {
        return security.canView('vehicles')
      }
    },
    RecentUnitsWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Recent`,
      caption: `Recently Monitored Units`,
      icon: 'hdd',
      secondaryIcon: 'repeat',
      canShow: () => {
        return security.canView('units')
      }
    },
    FindVehiclesWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Find`,
      caption: `Find Vehicles`,
      icon: 'car',
      secondaryIcon: 'search',
      canShow: () => {
        return security.canView('vehicles')
      }
    },
    FindUnitsWidget: {
      isOpen: false,
      openSide: 'right',
      width: 500,
      subMenu: `Find`,
      caption: `Find Units`,
      icon: 'hdd',
      secondaryIcon: 'search',
      canShow: () => {
        return security.canView('units')
      }
    },
    ToolsFindLocationWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: `Tools`,
      caption: `Find Location`,
      icon: 'search-location',
      secondaryIcon: 'tools',
      canShow: () => true
    },
    ToolsDirectionsWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: `Tools`,
      caption: `Get Directions`,
      icon: 'directions',
      secondaryIcon: 'tools',
      canShow: () => true
    },
    ToolsMeasurementWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: `Tools`,
      caption: `Measurement Ruler`,
      icon: 'ruler',
      secondaryIcon: 'tools',
      canShow: () => true
    },
    ToolsSendCommandWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: `Tools`,
      caption: `Send Command`,
      icon: 'ruler',
      secondaryIcon: 'tools',
      canShow: () => true
    },
    OptionsWidget: {
      isOpen: false,
      openSide: 'left',
      width: 500,
      subMenu: null,
      caption: `Options`,
      icon: 'cog',
      secondaryIcon: null,
      canShow: () => true
    }
  }
}

export const state = {
  widgetOrder: defaultWidgetOrder(),
  widgets: defaultWidgets(),
  rightWidgetOpen: false,
  leftWidgetOpen: false,
  rightWidgetWidth: 0,
  leftWidgetWidth: 0,
  changeCounter: 0
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the state of all the widgets
   *
   * @param state Object
   *
   * @returns Object
   */
  widgets (state) {
    return state.widgets
  },

  /**
   * Returns the preferred widget order (if displayed in lists for example)
   *
   * @param state Object
   *
   * @returns Array
   */
  widgetOrder (state) {
    return state.widgetOrder
  },

  /**
   * Returns true if a right side widget is open
   *
   * @param state
   *
   * @returns boolean
   */
  isRightWidgetOpen (state) {
    return state.rightWidgetOpen
  },

  /**
   * Returns true if a left side widget is open
   *
   * @param state
   * @returns boolean
   */
  isLeftWidgetOpen (state) {
    return state.leftWidgetOpen
  },

  /**
   * Returns the width of the right side widget
   *
   * @param state
   *
   * @returns integer
   */
  rightWidgetWidth (state) {
    return state.rightWidgetWidth
  },

  /**
   * Returns the width of the left side widget
   *
   * @param state
   *
   * @returns integer
   */
  leftWidgetWidth (state) {
    return state.leftWidgetWidth
  },

  /**
   * Returns the current state change counter
   *
   * @param state
   *
   * @returns integer
   */
  changeCounter (state) {
    return state.changeCounter
  }
}

export const mutations = {
  /**
   * Opens the specified widget.
   *
   * @param state  Object
   * @param widget string Name of the widget
   */
  open (state, widget) {
    if (state.widgets[widget].openSide === 'right') {
      // First close all other right side widgets as there
      // can't be more than one open at the same time
      Object.keys(state.widgets).forEach(widget => {
        if (state.widgets[widget].openSide === 'right') {
          state.widgets[widget].isOpen = false
        }
      })

      state.rightWidgetOpen = true
      state.rightWidgetWidth = state.widgets[widget].width
    } else if (state.widgets[widget].openSide === 'left') {
      // First close all other left side widgets as there
      // can't be more than one open at the same time
      Object.keys(state.widgets).forEach(widget => {
        if (state.widgets[widget].openSide === 'left') {
          state.widgets[widget].isOpen = false
        }
      })

      state.leftWidgetOpen = true
      state.leftWidgetWidth = state.widgets[widget].width
    }

    state.widgets[widget].isOpen = true
    state.changeCounter += 1
  },

  /**
   * Close the specified widget.
   *
   * @param state  Object
   * @param widget string Name of the widget
   */
  close (state, widget) {
    if (state.widgets[widget].openSide === 'right') {
      state.rightWidgetOpen = false
      state.rightWidgetWidth = 0
    } else if (state.widgets[widget].openSide === 'left') {
      state.leftWidgetOpen = false
      state.leftWidgetWidth = 0
    }

    state.widgets[widget].isOpen = false
    state.changeCounter += 1
  },

  /**
   * Close all open widgets.
   *
   * @param state  Object
   */
  closeAll (state) {
    Object.keys(state.widgets).forEach(widget => {
      state.widgets[widget].isOpen = false
    })

    state.rightWidgetOpen = false
    state.leftWidgetOpen = false

    state.rightWidgetWidth = 0
    state.leftWidgetWidth = 0
  },

  reset (state) {
    state.widgetOrder = defaultWidgetOrder()
    state.widgets = defaultWidgets()
    state.rightWidgetOpen = false
    state.leftWidgetOpen = false
    state.rightWidgetWidth = 0
    state.leftWidgetWidth = 0
    state.changeCounter = 0
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/monitoring/widgetControlModule.js